'use client';

/**
 * Media feed renderer. Shows a responsive thumbnail/clip grid of every
 * MediaBlob found across the feed's events (newest first), each rendered as a
 * real <img>/<video>/<audio> via <MediaBlob>. Falls back to JsonLogRenderer
 * for the raw stream below.
 */
import React, { useMemo, useState } from 'react';
import { JsonLogRenderer, type FeedRendererProps } from './JsonLogRenderer';
import { MediaBlob } from './MediaBlob';
import { findMediaBlobs, type MediaBlob as MediaBlobT, type AgentDebugEvent } from '@/lib/debug/types';

interface BlobItem {
  blob: MediaBlobT;
  ev: AgentDebugEvent;
}

export function MediaRenderer(props: FeedRendererProps) {
  const { events, callId } = props;
  const [showRaw, setShowRaw] = useState(false);

  const items = useMemo<BlobItem[]>(() => {
    const out: BlobItem[] = [];
    // newest event first
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      for (const blob of findMediaBlobs(ev.payload)) out.push({ blob, ev });
    }
    return out;
  }, [events]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center border-b border-white/[0.12] bg-[#0d0d0d] px-2 py-1.5 text-[11px]">
        <span className="text-slate-400">{items.length} media item{items.length === 1 ? '' : 's'}</span>
        <button
          onClick={() => setShowRaw((v) => !v)}
          className="ml-auto rounded border border-white/[0.12] px-1.5 py-0.5 text-slate-300 hover:bg-white/[0.06]"
        >
          {showRaw ? 'hide raw stream' : 'show raw stream'}
        </button>
      </div>
      <div className={`overflow-auto bg-[#0a0a0a] p-2 ${showRaw ? 'max-h-[55%]' : 'flex-1'}`}>
        {items.length === 0 ? (
          <div className="p-3 text-[11px] text-slate-600">no media items</div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
            {items.map((it, i) => (
              <div key={`${it.ev.id}-${i}`} className="rounded border border-white/[0.08] bg-[#0d0d0d] p-1">
                <div className="text-[10px] text-slate-500">
                  {new Date(it.ev.ts).toLocaleTimeString()} · {it.ev.kind}
                </div>
                <MediaBlob blob={it.blob} eventId={it.ev.id} callId={callId} />
              </div>
            ))}
          </div>
        )}
      </div>
      {showRaw && (
        <div className="min-h-0 flex-1 border-t border-white/[0.12]">
          <JsonLogRenderer {...props} />
        </div>
      )}
    </div>
  );
}

export default MediaRenderer;
